import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import styles from './ProductSheet_Characteristic.module.scss';
import Field from '../../widget/inputs/Field';
import Select from '../../widget/inputs/Select';
import WidgetFrame from '../../widget/navigation/WidgetFrame';
import CollapsiblePanel from '../../widget/windows/CollapsiblePanel';
import {
  ProductCharacteristic,
  initializedCharacteristic,
} from '../../../dataTypes/productManagment/types';

// Interface pour la configuration des types de caractéristiques
export interface CharacteristicTypeConfig {
  units: string[];
  requiresName: boolean;
  requiresUnit: boolean;
}

// Interface pour la configuration globale des types
export interface CharacteristicTypeOptionsConfig {
  [key: string]: CharacteristicTypeConfig;
}

// Configuration par défaut
export const DefaultCharacteristicTypeOptionsConfig: CharacteristicTypeOptionsConfig = {
  Dimension: { units: ['m', 'cm', 'mm'], requiresName: true, requiresUnit: true },
  Poids:     { units: ['kg', 'g'],      requiresName: true, requiresUnit: true },
  Durée:     { units: ['h', 'm', 's'],  requiresName: true, requiresUnit: true },
  Couleur:   { units: [''],             requiresName: true, requiresUnit: false },
  URL:       { units: [''],             requiresName: false, requiresUnit: false },
};

export interface ErrorMessagesForCharacteristic {
  value: string;
  name: string;
}

export interface ProductSheet_Characteristic_Props {
  defaultValue?: string;
  characteristic?: ProductCharacteristic;
  errorMessages: ErrorMessagesForCharacteristic[];
  indexInErrorMessages: number;
  title: string;
  width?: string;
  onChange?: (newCharacteristic: ProductCharacteristic) => void;
  onResize?: () => void;
  onDelete?: () => void;
  open?: boolean;
  withCollapsiblePanel?: boolean;
  characteristicTypeOptionsConfig?: CharacteristicTypeOptionsConfig;
  hasError?: boolean; // ← AJOUTER CETTE LIGNE
}

export const ProductSheet_Characteristic = ({
  defaultValue,
  characteristic,
  errorMessages,
  indexInErrorMessages,
  title,
  width = '100%',
  onChange,
  onResize,
  onDelete,
  open,
  withCollapsiblePanel = true,
  characteristicTypeOptionsConfig = DefaultCharacteristicTypeOptionsConfig,
  hasError = false // ← AJOUTER CETTE LIGNE
}: ProductSheet_Characteristic_Props) => {
  const isEditMode = !!characteristic;
  const initialCharacteristic = isEditMode ? characteristic : initializedCharacteristic();
  const [fields, setFields] = useState({
    name:  initialCharacteristic.name  || '',
    kind:  initialCharacteristic.kind  || '',
    unit:  initialCharacteristic.unit  || '',
    value: initialCharacteristic.value || '',
  });
  const [messages, setMessages] = useState({
    name:  errorMessages?.[indexInErrorMessages]?.name  ?? '',
    value: errorMessages?.[indexInErrorMessages]?.value ?? '',
  });

  useEffect(() => {
    setMessages({
      name:  errorMessages?.[indexInErrorMessages]?.name  ?? '',
      value: errorMessages?.[indexInErrorMessages]?.value ?? '',
    });
  }, [errorMessages]);

  useEffect(() => {
    if (fields.kind === 'URL') {
      setFields((prevFields) => ({
        ...prevFields,
        value: defaultValue ?? '',
      }));
    }
  }, [fields.kind, defaultValue]);

  const handleKindChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;
    setFields((prev) => {
      const updatedFields = { ...prev, kind: newValue };
      const config = characteristicTypeOptionsConfig[newValue];
      if (config) {
        updatedFields.unit = config.units[0] || '';
        // Sans "Nom" éditable (requiresName: false), le champ "Nom" reste caché —
        // mais le type EST le nom : les processeurs d'achat qui lisent une
        // caractéristique par son nom (ex. "Quantité" pour
        // SaleActionProcessor_Lovelight_TV_Buying_creator_capacity) le
        // trouveraient sinon vide, jamais "Quantité". Sans effet sur les types
        // qui affichent déjà le champ "Nom" (requiresName: true).
        if (!config.requiresName) {
          updatedFields.name = newValue;
        }
      }
      if (newValue === 'URL') {
        updatedFields.value = defaultValue ?? '';
      }
      notifyCharacteristicChange({ ...initialCharacteristic, ...updatedFields });
      return updatedFields;
    });
  };

  const handleUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;
    setFields((prev) => {
      const updatedFields = { ...prev, unit: newValue };
      notifyCharacteristicChange({ ...initialCharacteristic, ...updatedFields });
      return updatedFields;
    });
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFields((prev) => {
      const updatedFields = { ...prev, name: newValue };
      notifyCharacteristicChange({ ...initialCharacteristic, ...updatedFields });
      return updatedFields;
    });
    setMessages((prev) => ({ ...prev, name: newValue === '' ? 'Il manque le nom' : '' }));
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFields((prev) => {
      const updatedFields = { ...prev, value: newValue };
      notifyCharacteristicChange({ ...initialCharacteristic, ...updatedFields });
      return updatedFields;
    });
    setMessages((prev) => ({ ...prev, value: newValue === '' ? 'Il manque la valeur' : '' }));
  };

  const notifyCharacteristicChange = (newCharacteristic: ProductCharacteristic) => {
    if (onChange) {
      onChange(newCharacteristic);
    }
  };
/*
  const renderContent = () => {
    const config = characteristicTypeOptionsConfig[fields.kind];
    const requiresName = config?.requiresName ?? false;
    const requiresUnit = config?.requiresUnit ?? false;
    const unitOptions = config?.units || [];

    return (
      <div className={classNames(styles.contain)}>
        <div className={classNames(styles.line_1a)}>
          <WidgetFrame
            name="Type :"
            labelTextPosition="right"
            classNameGrid={classNames(styles.grid_frame)}
          >
            <Select
              choices={Object.keys(characteristicTypeOptionsConfig)}
              value={fields.kind}
              onChange={handleKindChange}
            />
          </WidgetFrame>
        </div>

        {requiresUnit && (
          <div className={classNames(styles.line_1b)}>
            <WidgetFrame
              name="Unité :"
              labelTextPosition="right"
              classNameGrid={classNames(styles.grid_frame)}
            >
              <Select
                choices={unitOptions}
                value={fields.unit}
                onChange={handleUnitChange}
              />
            </WidgetFrame>
          </div>
        )}

        {requiresName && (
          <div className={classNames(styles.line_2)}>
            <WidgetFrame
              name="Nom :"
              labelTextPosition="right"
              error={messages.name}
              classNameGrid={classNames(styles.grid_frame)}
            >
              <Field
                type="text"
                value={fields.name}
                onChange={handleNameChange}
              />
            </WidgetFrame>
          </div>
        )}

        <div className={classNames(!requiresName ? styles.line_2 : styles.line_3)}>
          <WidgetFrame
            name="Valeur :"
            labelTextPosition="right"
            error={messages.value}
            classNameGrid={classNames(styles.grid_frame)}
          >
            <Field
              type="text"
              value={fields.value}
              onChange={handleValueChange}
            />
          </WidgetFrame>
        </div>
      </div>
    );
  };

*/

 const renderContent = () => {
    const config = characteristicTypeOptionsConfig[fields.kind];
    const requiresName = config?.requiresName ?? false;
    const requiresUnit = config?.requiresUnit ?? false;
    const unitOptions = config?.units || [];

    return (
      <div className={classNames(styles.contain)} style={{ position: 'relative' }}>
        {/* ✅ Message d'erreur SI hasError === true 
        {hasError && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#dc2626',
            fontSize: '14px',
            fontWeight: '600',
            textAlign: 'center',
            pointerEvents: 'none',
            zIndex: 10,
            whiteSpace: 'nowrap'
          }}>
            ⚠️ Caractéristique incomplète
          </div>
        )}
*/}
        <div className={classNames(styles.line_1a)}>
          <WidgetFrame
            name="Type :"
            labelTextPosition="right"
            classNameGrid={classNames(styles.grid_frame)}
          >
            <Select
              choices={Object.keys(characteristicTypeOptionsConfig)}
              value={fields.kind}
              onChange={handleKindChange}
            />
          </WidgetFrame>
        </div>

        {requiresUnit && (
          <div className={classNames(styles.line_1b)}>
            <WidgetFrame
              name="Unité :"
              labelTextPosition="right"
              classNameGrid={classNames(styles.grid_frame)}
            >
              <Select
                choices={unitOptions}
                value={fields.unit}
                onChange={handleUnitChange}
              />
            </WidgetFrame>
          </div>
        )}

        {requiresName && (
          <div className={classNames(styles.line_2)}>
            <WidgetFrame
              name="Nom :"
              labelTextPosition="right"
              error={messages.name}
              classNameGrid={classNames(styles.grid_frame)}
            >
              <Field
                type="text"
                value={fields.name}
                onChange={handleNameChange}
              />
            </WidgetFrame>
          </div>
        )}

        <div className={classNames(!requiresName ? styles.line_2 : styles.line_3)}>
          <WidgetFrame
            name="Valeur :"
            labelTextPosition="right"
            error={messages.value}
            classNameGrid={classNames(styles.grid_frame)}
          >
            <Field
              type="text"
              value={fields.value}
              onChange={handleValueChange}
            />
          </WidgetFrame>
        </div>
      </div>
    );
  };
  
  
  if (withCollapsiblePanel) {
    return (
      <div className={classNames(styles.root)}>
        <CollapsiblePanel
          title={title}
          onResize={onResize}
          onDelete={onDelete}
          open={open}
        >
          {renderContent()}
        </CollapsiblePanel>
      </div>
    );
  } else {
    return (
      <div className={classNames(styles.root)}>
        {renderContent()}
      </div>
    );
  }
};

export default ProductSheet_Characteristic;
